import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectLoggedInUserId } from "../../../../../Store/features/user/userSlice";
import {
	addSpotlightProductComment,
	likeSpotlightProductComment,
	replySpotlightProductComment,
	voteComment,
	downVoteComment,
	upvoteReply,
	downvoteReply,
	deleteStartupSpotlightProductComment,
	deleteStartupSpotlightProductReply,
} from "../../../../../Service/user";
import { toast } from "react-hot-toast";
import ThumbUpIcon from "../../../../../Images/Startup-Spotlight/ThumbUpIcon.png";
import RoastIcon from "../../../../../Images/Startup-Spotlight/RoastIcon.png";
import { FaThumbsUp, FaThumbsDown, FaReply, FaAngleDown, FaAngleUp, FaTrash } from "react-icons/fa";
import "./Comments.scss";

const Comments = ({ comments: initialComments, productId, firstComment, onCommentAdded }) => {
	const loggedInUserId = useSelector(selectLoggedInUserId);
	const loggedInUser = useSelector((state) => state.user.loggedInUser);
	const navigate = useNavigate();
	const [commentText, setCommentText] = useState("");
	const [isSupporting, setIsSupporting] = useState(true);
	const [filter, setFilter] = useState("All");
	const [comments, setComments] = useState(initialComments || []);
	const [activeReplyCommentId, setActiveReplyCommentId] = useState(null);
	const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
	const [showRepliesMap, setShowRepliesMap] = useState({});
	const filterOptions = [
		"All",
		"Support",
		"Roast",
		"Most Liked",
		"Most Recent",
		"Most Replied",
		"Least Replied",
		"Least Liked",
	];

	const [loadingActions, setLoadingActions] = useState({
		comment: false,
		like: new Set(),
		vote: new Set(),
		reply: new Set(),
	});
	const [replyInput, setReplyInput] = useState({
		isOpen: false,
		commentId: null,
		text: "",
	});

	// Update local state when props change
	useEffect(() => {
		// Make sure upvotes and downvotes are numbers
		const processedComments = (initialComments || []).map(comment => {
			// Skip if comment is undefined
			if (!comment) return null;
			
			return {
				...comment,
				upvotes: Array.isArray(comment?.upvotes) ? comment.upvotes.length : (comment?.upvotes || 0),
				downvotes: Array.isArray(comment?.downvotes) ? comment.downvotes.length : (comment?.downvotes || 0),
				reply: Array.isArray(comment?.reply) ? comment.reply.map(reply => {
					// Skip if reply is undefined
					if (!reply) return null;
					
					return {
						...reply,
						upvotes: Array.isArray(reply?.upvotes) ? reply.upvotes.length : (reply?.upvotes || 0),
						downvotes: Array.isArray(reply?.downvotes) ? reply.downvotes.length : (reply?.downvotes || 0)
					};
				}).filter(Boolean) : []  // Filter out any null values
			};
		}).filter(Boolean);  // Filter out any null values
		
		setComments(processedComments);
	}, [initialComments]);

	useEffect(() => {
		if (firstComment) {
			// Process first comment to ensure upvotes/downvotes are numbers
			const processedFirstComment = {
				...firstComment,
				upvotes: Array.isArray(firstComment?.upvotes) ? firstComment.upvotes.length : (firstComment?.upvotes || 0),
				downvotes: Array.isArray(firstComment?.downvotes) ? firstComment.downvotes.length : (firstComment?.downvotes || 0),
				reply: Array.isArray(firstComment?.reply) ? firstComment.reply.map(reply => {
					// Skip if reply is undefined
					if (!reply) return null;
					
					return {
						...reply,
						upvotes: Array.isArray(reply?.upvotes) ? reply.upvotes.length : (reply?.upvotes || 0),
						downvotes: Array.isArray(reply?.downvotes) ? reply.downvotes.length : (reply?.downvotes || 0)
					};
				}).filter(Boolean) : [] // Filter out any null values
			};
			setComments([processedFirstComment, ...comments]);
		}
	}, [firstComment]);

	// Filtered and sorted comments based on selected filter
	const filteredAndSortedComments = useMemo(() => {
		// Filter out undefined or null comments
		let processedComments = [...comments].filter(Boolean);

		switch (filter) {
			case "Support":
				processedComments = processedComments.filter(
					(comment) => comment?.isSupporting
				);
				break;
			case "Roast":
				processedComments = processedComments.filter(
					(comment) => comment && comment.isSupporting === false
				);
				break;
			case "Most Liked":
				processedComments.sort(
					(a, b) => ((b?.upvotes || 0) - (a?.upvotes || 0))
				);
				break;
			case "Most Recent":
				processedComments.sort(
					(a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
				);
				break;
			case "Most Replied":
				processedComments.sort((a, b) => ((b?.replies || 0) - (a?.replies || 0)));
				break;
			case "Least Replied":
				processedComments.sort((a, b) => ((a?.replies || 0) - (b?.replies || 0)));
				break;
			case "Least Liked":
				processedComments.sort(
					(a, b) => ((a?.upvotes || 0) - (b?.upvotes || 0))
				);
				break;
			default: // "All"
				break;
		}
		return processedComments;
	}, [comments, filter]);

	const redirectToLogin = () => {
		navigate('/login', { state: { from: window.location.pathname } });
	};

	const handleSendComment = async () => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!commentText.trim()) {
			toast.error("Please enter a comment");
			return;
		}

		setLoadingActions({ ...loadingActions, comment: true });

		// Create optimistic comment
		const optimisticComment = {
			_id: `temp-${Date.now()}`,
			comment: commentText,
			isSupporting,
			senderId: {
				_id: loggedInUserId,
				firstName: loggedInUser?.firstName || "",
				lastName: loggedInUser?.lastName || "",
				profilePicture: loggedInUser?.profilePicture || null,
			},
			likes: [],
			upvotes: 0,
			downvotes: 0,
			replies: 0,
			reply: [],
			isOptimistic: true,
			createdAt: new Date().toISOString(),
		};

		// Add optimistic comment to UI
		setComments(prevComments => [optimisticComment, ...prevComments]);
		setCommentText("");

		try {
			const commentData = {
				comment: commentText,
				isSupporting,
			};
			const response = await addSpotlightProductComment(productId, commentData);

			if (response.status === 200) {
				// Process the response to ensure upvotes and downvotes are numbers
				const processedComment = {
					...response.comment,
					senderId: {
						_id: loggedInUserId,
						firstName: loggedInUser?.firstName || "",
						lastName: loggedInUser?.lastName || "",
						profilePicture: loggedInUser?.profilePicture || null,
					},
					upvotes: Array.isArray(response.comment.upvotes) ? response.comment.upvotes.length : (response.comment.upvotes || 0),
					downvotes: Array.isArray(response.comment.downvotes) ? response.comment.downvotes.length : (response.comment.downvotes || 0),
					reply: Array.isArray(response.comment.reply) ? response.comment.reply.map(reply => ({
						...reply,
						upvotes: Array.isArray(reply.upvotes) ? reply.upvotes.length : (reply.upvotes || 0),
						downvotes: Array.isArray(reply.downvotes) ? reply.downvotes.length : (reply.downvotes || 0)
					})) : []
				};
				
				// Replace optimistic comment with real one and ensure no comments are lost
				setComments((prevComments) => {
					// Remove the optimistic comment
					const filteredComments = prevComments.filter(comment => comment._id !== optimisticComment._id);
					// Add the processed comment at the beginning
					return [processedComment, ...filteredComments];
				});
				
				if (onCommentAdded) {
					onCommentAdded(processedComment);
				}
				
				toast.success("Comment added successfully");
			}
		} catch (error) {
			console.error("Error:", error);
			// Remove optimistic comment on error
			setComments((prevComments) =>
				prevComments.filter((comment) => comment._id !== optimisticComment._id)
			);
			toast.error(error.message || "Failed to add comment");
		} finally {
			setLoadingActions({ ...loadingActions, comment: false });
		}
	};

	const handleVoteComment = async (commentId, isUpvote) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!commentId) return;
		
		// Find the current comment
		const currentComment = comments.find(comment => comment._id === commentId);
		if (!currentComment) return;
		
		// Update loading state
		setLoadingActions((prev) => ({
			...prev,
			vote: new Set([...prev.vote, commentId]),
		}));
		
		// Store original comment for potential restore
		const originalComment = { ...currentComment };
		
		// Calculate vote changes based on current state
		const userVote = currentComment.userVote || null;
		let upvoteDelta = 0;
		let downvoteDelta = 0;
		let newUserVote = null;
		
		if (isUpvote) {
			// Handle upvote logic
			if (userVote === 'up') {
				// Remove upvote if already upvoted
				upvoteDelta = -1;
				newUserVote = null;
			} else {
				// Add upvote and remove downvote if exists
				upvoteDelta = 1;
				if (userVote === 'down') {
					downvoteDelta = -1;
				}
				newUserVote = 'up';
			}
		} else {
			// Handle downvote logic
			if (userVote === 'down') {
				// Remove downvote if already downvoted
				downvoteDelta = -1;
				newUserVote = null;
			} else {
				// Add downvote and remove upvote if exists
				downvoteDelta = 1;
				if (userVote === 'up') {
					upvoteDelta = -1;
				}
				newUserVote = 'down';
			}
		}
		
		// Optimistically update UI
		setComments((prevComments) =>
			prevComments.map((comment) => {
				if (comment._id === commentId) {
					// Ensure upvotes and downvotes are numbers
					const currentUpvotes = typeof comment.upvotes === 'number' ? comment.upvotes : 0;
					const currentDownvotes = typeof comment.downvotes === 'number' ? comment.downvotes : 0;
					
					return {
						...comment,
						upvotes: Math.max(0, currentUpvotes + upvoteDelta),
						downvotes: Math.max(0, currentDownvotes + downvoteDelta),
						userVote: newUserVote
					};
				}
				return comment;
			})
		);
		
		try {
			// Choose the appropriate API endpoint
			const apiFunction = isUpvote ? voteComment : downVoteComment;
			const response = await apiFunction(commentId);
			
			if (response.status !== 200) {
				throw new Error(response.message || "Error updating vote");
			}
			
			// Process the response to ensure consistent data structure
			const processedComment = response.comment ? {
				...response.comment,
				upvotes: Array.isArray(response.comment.upvotes) ? response.comment.upvotes.length : response.comment.upvotes || 0,
				downvotes: Array.isArray(response.comment.downvotes) ? response.comment.downvotes.length : response.comment.downvotes || 0,
				userVote: newUserVote // Maintain the vote state we calculated
			} : null;
			
			if (processedComment) {
				// Update with server response
				setComments((prevComments) =>
					prevComments.map((comment) => {
						if (comment._id === commentId) {
							return {
								...comment,
								...processedComment,
								isSupporting: comment.isSupporting // Preserve isSupporting
							};
						}
						return comment;
					})
				);
			}
		} catch (error) {
			console.error("Error voting:", error);
			toast.error("Failed to register vote");
			
			// Revert optimistic update on error
			setComments((prevComments) =>
				prevComments.map((comment) => 
					comment._id === commentId ? originalComment : comment
				)
			);
		} finally {
			setLoadingActions((prev) => {
				const newSet = new Set([...prev.vote]);
				newSet.delete(commentId);
				return { ...prev, vote: newSet };
			});
		}
	};

	const handleReplyClick = (commentId) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		// Toggle the active reply comment ID
		setActiveReplyCommentId((prevId) =>
			prevId === commentId ? null : commentId
		);
		
		setReplyInput({
			isOpen: activeReplyCommentId !== commentId,
			commentId: commentId,
			text: "",
		});
	};

	const toggleShowReplies = (commentId) => {
		setShowRepliesMap(prev => ({
			...prev,
			[commentId]: !prev[commentId]
		}));
	};

	const handleSubmitReply = async (commentId) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!replyInput.text.trim()) {
			toast.error("Please enter a reply");
			return;
		}

		setLoadingActions((prev) => ({
			...prev,
			reply: new Set([...prev.reply, commentId]),
		}));

		// Create optimistic reply with fields matching the API response structure
		const optimisticReply = {
			_id: `temp-reply-${Date.now()}`,
			text: replyInput.text, // Using text as the API expects
			senderId: {
				_id: loggedInUserId,
				firstName: loggedInUser?.firstName || "",
				lastName: loggedInUser?.lastName || "",
				profilePicture: loggedInUser?.profilePicture || null,
			},
			upvotes: 0,
			downvotes: 0,
			isOptimistic: true,
			createdAt: new Date().toISOString(),
		};

		// Show replies if they were hidden
		setShowRepliesMap(prev => ({
			...prev,
			[commentId]: true
		}));

		setComments((prevComments) =>
			prevComments.map((comment) => {
				if (comment._id === commentId) {
					// Ensure reply array exists
					const currentReplies = Array.isArray(comment.reply) ? comment.reply : [];
					return {
						...comment,
						reply: [...currentReplies, optimisticReply],
						replies: (comment.replies || 0) + 1,
					};
				}
				return comment;
			})
		);

		setReplyInput({
			...replyInput,
			text: "",
		});

		try {
			// Send the text field to the API
			const response = await replySpotlightProductComment(commentId, {
				reply: replyInput.text, // This is the payload that the API expects
			});

			if (response.status === 200) {
				// Process the reply to ensure upvotes and downvotes are numbers
				const processedReply = {
					...response.reply,
					upvotes: Array.isArray(response.reply.upvotes) ? response.reply.upvotes.length : (response.reply.upvotes || 0),
					downvotes: Array.isArray(response.reply.downvotes) ? response.reply.downvotes.length : (response.reply.downvotes || 0)
				};
				
				// Update with real reply from server
				setComments((prevComments) =>
					prevComments.map((comment) => {
						if (comment._id === commentId) {
							// Ensure reply array exists and remove optimistic reply
							const filteredReplies = Array.isArray(comment.reply) 
								? comment.reply.filter(reply => reply._id !== optimisticReply._id)
								: [];
							
							// Add the real reply from server
							return {
								...comment,
								reply: [...filteredReplies, processedReply],
								replies: (comment.replies || 0),
							};
						}
						return comment;
					})
				);
				toast.success("Reply added successfully");
			}
		} catch (error) {
			console.error("Error adding reply:", error);
			// Remove optimistic reply
			setComments((prevComments) =>
				prevComments.map((comment) => {
					if (comment._id === commentId) {
						return {
							...comment,
							reply: Array.isArray(comment.reply) 
								? comment.reply.filter(reply => reply._id !== optimisticReply._id) 
								: [],
							replies: Math.max(0, (comment.replies || 0) - 1),
						};
					}
					return comment;
				})
			);
			toast.error("Failed to add reply");
		} finally {
			setActiveReplyCommentId(null);
			setLoadingActions((prev) => {
				const newSet = new Set([...prev.reply]);
				newSet.delete(commentId);
				return { ...prev, reply: newSet };
			});
		}
	};

	const handleReplyVote = async (commentId, replyId, isUpvote) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!replyId || !commentId) return;
		
		// Find the current comment and reply
		const currentComment = comments.find(comment => comment._id === commentId);
		if (!currentComment) return;
		
		const currentReply = currentComment.reply?.find(reply => reply._id === replyId);
		if (!currentReply) return;
		
		// Update loading state
		setLoadingActions((prev) => ({
			...prev,
			vote: new Set([...prev.vote, replyId]),
		}));
		
		// Store original reply for potential restore
		const originalReply = { ...currentReply };
		const originalComment = { ...currentComment };
		
		// Calculate vote changes based on current state
		const userVote = currentReply.userVote || null;
		let upvoteDelta = 0;
		let downvoteDelta = 0;
		let newUserVote = null;
		
		if (isUpvote) {
			// Handle upvote logic
			if (userVote === 'up') {
				// Remove upvote if already upvoted
				upvoteDelta = -1;
				newUserVote = null;
			} else {
				// Add upvote and remove downvote if exists
				upvoteDelta = 1;
				if (userVote === 'down') {
					downvoteDelta = -1;
				}
				newUserVote = 'up';
			}
		} else {
			// Handle downvote logic
			if (userVote === 'down') {
				// Remove downvote if already downvoted
				downvoteDelta = -1;
				newUserVote = null;
			} else {
				// Add downvote and remove upvote if exists
				downvoteDelta = 1;
				if (userVote === 'up') {
					upvoteDelta = -1;
				}
				newUserVote = 'down';
			}
		}
		
		// Optimistically update UI
		setComments((prevComments) =>
			prevComments.map((comment) => {
				if (comment._id === commentId) {
					const updatedReplies = comment.reply?.map(reply => {
						if (reply._id === replyId) {
							// Ensure upvotes and downvotes are numbers
							const currentUpvotes = typeof reply.upvotes === 'number' ? reply.upvotes : 0;
							const currentDownvotes = typeof reply.downvotes === 'number' ? reply.downvotes : 0;
							
							return {
								...reply,
								upvotes: Math.max(0, currentUpvotes + upvoteDelta),
								downvotes: Math.max(0, currentDownvotes + downvoteDelta),
								userVote: newUserVote
							};
						}
						return reply;
					});
					
					return {
						...comment,
						reply: updatedReplies || []
					};
				}
				return comment;
			})
		);
		
		try {
			// Use the specific upvoteReply and downvoteReply functions
			const apiFunction = isUpvote ? upvoteReply : downvoteReply;
			const response = await apiFunction(commentId, replyId);
			
			if (response.status !== 200) {
				throw new Error(response.message || "Error updating vote");
			}
			
			// Process the response reply to ensure consistent data structure
			if (response.reply) {
				const processedReply = {
					...response.reply,
					upvotes: Array.isArray(response.reply.upvotes) ? response.reply.upvotes.length : response.reply.upvotes || 0,
					downvotes: Array.isArray(response.reply.downvotes) ? response.reply.downvotes.length : response.reply.downvotes || 0,
					userVote: newUserVote // Maintain the vote state we calculated
				};
				
				// Update with server response
				setComments((prevComments) =>
					prevComments.map((comment) => {
						if (comment._id === commentId) {
							const updatedReplies = comment.reply?.map(reply => {
								if (reply._id === replyId) {
									return {
										...reply,
										...processedReply
									};
								}
								return reply;
							}) || [];
							
							return {
								...comment,
								reply: updatedReplies
							};
						}
						return comment;
					})
				);
			}
		} catch (error) {
			console.error("Error voting on reply:", error);
			toast.error("Failed to register vote on reply");
			
			// Revert optimistic update on error
			setComments((prevComments) =>
				prevComments.map((comment) => {
					if (comment._id === commentId) {
						return {
							...originalComment,
							reply: (originalComment.reply || []).map(reply => 
								reply._id === replyId ? originalReply : reply
							)
						};
					}
					return comment;
				})
			);
		} finally {
			setLoadingActions((prev) => {
				const newSet = new Set([...prev.vote]);
				newSet.delete(replyId);
				return { ...prev, vote: newSet };
			});
		}
	};

	const handleDeleteComment = async (productId, commentId) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!commentId) return;
		
		// Find the comment that will be deleted
		const commentToDelete = comments.find(comment => comment._id === commentId);
		if (!commentToDelete) return;
		
		// Optimistically remove the comment from UI
		setComments(prevComments => prevComments.filter(comment => comment._id !== commentId));
		
		try {
			const response = await deleteStartupSpotlightProductComment(productId, commentId);
			if (response.status !== 200) {
				throw new Error(response.message || "Failed to delete comment");
			}
			
			toast.success("Comment deleted successfully");
		} catch (error) {
			console.error("Error deleting comment:", error);
			toast.error(error.message || "Failed to delete comment");
			
			// Restore the comment if deletion fails
			setComments(prevComments => [commentToDelete, ...prevComments]);
		}
	};
	
	const handleDeleteReply = async (commentId, replyId) => {
		if (!loggedInUserId) {
			redirectToLogin();
			return;
		}

		if (!commentId || !replyId) return;
		
		// Find the comment and reply to be deleted
		const currentComment = comments.find(comment => comment._id === commentId);
		if (!currentComment) return;
		
		const replyToDelete = currentComment.reply?.find(reply => reply._id === replyId);
		if (!replyToDelete) return;
		
		// Optimistically update UI
		setComments(prevComments =>
			prevComments.map(comment => {
				if (comment._id === commentId) {
					return {
						...comment,
						reply: comment.reply.filter(reply => reply._id !== replyId),
						replies: Math.max(0, (comment.replies || 0) - 1)
					};
				}
				return comment;
			})
		);
		
		try {
			const response = await deleteStartupSpotlightProductReply(commentId, replyId);
			
			if (response.status !== 200) {
				throw new Error(response.message || "Failed to delete reply");
			}
			
			toast.success("Reply deleted successfully");
		} catch (error) {
			console.error("Error deleting reply:", error);
			toast.error(error.message || "Failed to delete reply");
			
			// Restore the reply if deletion fails
			setComments(prevComments =>
				prevComments.map(comment => {
					if (comment._id === commentId) {
						return {
							...comment,
							reply: [...comment.reply, replyToDelete],
							replies: (comment.replies || 0) + 1
						};
					}
					return comment;
				})
			);
		}
	};

	const isReplyLoading = (commentId) => loadingActions.reply.has(commentId);
	const isLikeLoading = (commentId) => loadingActions.like.has(commentId);
	const isVoteLoading = (commentId) => loadingActions.vote.has(commentId);

	return (
		<div className="spotlight-comments-container">
			<h2 className="comments-heading">Comments</h2>

			{/* Comment input section */}
			{loggedInUserId ? (
				<div className="comment-input-container">
					<div className="comment-avatar">
						<img
							src={
								loggedInUser?.profilePicture ||
								`https://ui-avatars.com/api/?name=${loggedInUser?.firstName || "U"}+${
									loggedInUser?.lastName || "S"
								}&background=random`
							}
							alt="user avatar"
						/>
					</div>
					<div className="comment-input-wrapper">
						<div className="comment-input">
							<textarea
								placeholder="Add a comment..."
								value={commentText}
								onChange={(e) => setCommentText(e.target.value)}
							/>
						</div>
						<div className="comment-actions">
							<div className="comment-type-toggles">
								<button
									className={`toggle-button support ${
										isSupporting ? "active" : ""
									}`}
									onClick={() => setIsSupporting(true)}
								>
									<img src={ThumbUpIcon} alt="Support" />
									Support
								</button>
								<button
									className={`toggle-button roast ${
										!isSupporting ? "active" : ""
									}`}
									onClick={() => setIsSupporting(false)}
								>
									<img src={RoastIcon} alt="Roast" />
									Roast
								</button>
							</div>
							<button
								className="send-button"
								onClick={handleSendComment}
								disabled={loadingActions.comment}
							>
								{loadingActions.comment ? "Sending..." : "Comment"}
							</button>
						</div>
					</div>
				</div>
			) : (
				<div className="login-prompt">
					<p>You need to log in to comment on this product.</p>
					<button className="login-button" onClick={redirectToLogin}>
						Log in to comment
					</button>
				</div>
			)}

			{/* Filter dropdown */}
			<div className="filter-dropdown">
				<button
					className="filter-dropdown-toggle"
					onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
				>
					<span>Filter: {filter}</span>
					<span className="dropdown-arrow">
						{isFilterDropdownOpen ? "▲" : "▼"}
					</span>
				</button>
				{isFilterDropdownOpen && (
					<ul className="filter-dropdown-menu">
						{filterOptions.map((option) => (
							<li
								key={option}
								className={filter === option ? "active" : ""}
								onClick={() => {
									setFilter(option);
									setIsFilterDropdownOpen(false);
								}}
							>
								{option}
							</li>
						))}
					</ul>
				)}
			</div>

			{/* Comments list */}
			<div className="comments-list">
				{filteredAndSortedComments.length === 0 ? (
					<div className="no-comments">
						<p>No comments yet. Be the first to comment!</p>
					</div>
				) : (
					filteredAndSortedComments.map((comment) => (
						<div 
							className={`comment-item ${comment.isSupporting ? 'support' : 'roast'}`} 
							key={comment._id}
						>
							<div className="comment-main">
								<div className="comment-avatar">
									<img
										src={
											comment.senderId?.profilePicture ||
											`https://ui-avatars.com/api/?name=${
												comment.senderId?.firstName || "U"
											}+${
												comment.senderId?.lastName || "S"
											}&background=random`
										}
										alt="user avatar"
									/>
								</div>
								<div className="comment-content">
									<div className="comment-header">
										<span className="commenter-name">
											{comment.senderId?.firstName || ""}{" "}
											{comment.senderId?.lastName || ""}
										</span>
										<span className="comment-time">
											{new Date(comment.createdAt).toLocaleDateString()}
										</span>
									</div>
									<div className="comment-text">
										<p>{comment.comment}</p>
									</div>
									
									<div className="comment-actions">
										<div className="vote-buttons">
											<button 
												className={`upvote-btn ${comment.userVote === 'up' ? 'active' : ''}`}
												onClick={() => handleVoteComment(comment._id, true)}
												disabled={isVoteLoading(comment._id)}
											>
												<FaThumbsUp /> 
												<span>{comment.upvotes.length || 0}</span>
											</button>
											
											<button 
												className={`downvote-btn ${comment.userVote === 'down' ? 'active' : ''}`}
												onClick={() => handleVoteComment(comment._id, false)}
												disabled={isVoteLoading(comment._id)}
											>
												<FaThumbsDown /> 
												<span>{comment.downvotes.length || 0}</span>
											</button>
										</div>
										
										<button
											className="reply-btn"
											onClick={() => handleReplyClick(comment._id)}
										>
											<FaReply /> Reply
										</button>
										
										{(comment.replies > 0 || (comment.reply && comment.reply.length > 0)) && (
											<button 
												className="show-replies-btn"
												onClick={() => toggleShowReplies(comment._id)}
											>
												{showRepliesMap[comment._id] ? (
													<><FaAngleUp /> Hide Replies ({comment.replies || comment.reply?.length || 0})</>
												) : (
													<><FaAngleDown /> Show Replies ({comment.replies || comment.reply?.length || 0})</>
												)}
											</button>
										)}
										
										{comment.senderId?._id === loggedInUserId && (
											<button
												className="delete-btn"
												onClick={() => handleDeleteComment(productId, comment._id)}
											>
												<FaTrash /> Delete
											</button>
										)}
									</div>
								</div>
							</div>

							{/* Reply input */}
							{activeReplyCommentId === comment._id && (
								<div className="reply-input-container">
									<textarea
										placeholder="Write a reply..."
										value={replyInput.text}
										onChange={(e) =>
											setReplyInput({ ...replyInput, text: e.target.value })
										}
									/>
									<div className="reply-action-buttons">
										<button
											className="cancel-reply"
											onClick={() => setActiveReplyCommentId(null)}
										>
											Cancel
										</button>
										<button
											className="submit-reply"
											onClick={() => handleSubmitReply(comment._id)}
											disabled={isReplyLoading(comment._id)}
										>
											{isReplyLoading(comment._id) ? "Sending..." : "Reply"}
										</button>
									</div>
								</div>
							)}

							{/* Reply list */}
							{comment.reply && comment.reply.length > 0 && showRepliesMap[comment._id] && (
								<div className="replies-list">
									{comment.reply.map((reply) => {
										const firstName = reply?.senderId?.firstName || "Anonymous";
										const lastName = reply?.senderId?.lastName || "";
										const profilePic = reply?.senderId?.profilePicture;
										const replyContent = reply.text || reply.comment || "";
										
										return (
											<div 
												key={reply._id} 
												className="reply-item"
											>
												<div className="reply-avatar">
													<img
														src={
															profilePic ||
															`https://ui-avatars.com/api/?name=${
																firstName.charAt(0)}+${
																lastName.charAt(0)
															}&background=random`
														}
														alt="user avatar"
													/>
												</div>
												<div className="reply-content">
													<div className="reply-header">
														<span className="replier-name">
															{firstName} {lastName}
														</span>
														{/* <span className="reply-time">
															{new Date(reply.createdAt).toLocaleDateString()}
														</span> */}
													</div>
													<div className="reply-text">
														<p>{replyContent}</p>
													</div>
													<div className="reply-actions">
														<div className="vote-buttons">
															<button 
																className={`upvote-btn ${reply.userVote === 'up' ? 'active' : ''}`}
																onClick={() => handleReplyVote(comment._id, reply._id, true)}
																disabled={isVoteLoading(reply._id)}
															>
																<FaThumbsUp /> 
																<span>{reply.upvotes.length || 0}</span>
															</button>
															
															<button 
																className={`downvote-btn ${reply.userVote === 'down' ? 'active' : ''}`}
																onClick={() => handleReplyVote(comment._id, reply._id, false)}
																disabled={isVoteLoading(reply._id)}
															>
																<FaThumbsDown /> 
																<span>{reply.downvotes.length || 0}</span>
															</button>


															{reply.senderId?._id === loggedInUserId && (
															<button
																className="delete-btn"
																onClick={() => handleDeleteReply(comment._id, reply._id)}
															>
																<FaTrash /> Delete
															</button>
														)}
														</div>
														
														
													</div>
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					))
				)}
			</div>
		</div>
	);
};

export default Comments;
